/**
 * Sprint 24.17a (S24.17a.20–22) — pickup-toast observer.
 *
 * The "Architect picked up Sprint X" pickup happens server-side (the agent's
 * next run, off the task write). The client surfaces it by watching the
 * same system activity feed the Live Activity Line reads and firing the sticky
 * pickup toast (Sprint 24.12 chrome, extended in primitives) for any FRESH
 * pickup. Phase/progress + title update as the agent advances (S24.17a.22),
 * because the feed re-derives the toast on each 5s poll.
 *
 * Fire-once-per-phase: a toast is (re)fired only when its task pickup is new
 * OR its progress changed, so a dismissed toast doesn't reappear on the next
 * poll, while a real phase advance does update it.
 */

import { useEffect, useRef } from "react";
import { useSystemActivityFeed } from "./use-active-agent-for";
import { showPickupToast } from "../components/primitives";
import { pickupToastFromActivity } from "../lib/pickup-toast";
import type { ActivityLike } from "../lib/active-activity";

/** Only surface pickups from roughly the last few minutes (don't toast history). */
const FRESH_TOAST_MS = 5 * 60_000;

function ageMs(a: ActivityLike, now: number): number {
  const iso = a.occurred_at ?? a.created_at ?? null;
  const ms = iso ? Date.parse(iso) : NaN;
  return Number.isNaN(ms) ? Infinity : now - ms;
}

export function usePickupToastObserver(): void {
  const feed = useSystemActivityFeed();
  // stableId → last fired progress (so we don't re-fire an unchanged/dismissed toast).
  const fired = useRef<Map<string, number | undefined>>(new Map());

  useEffect(() => {
    const activities = feed.data;
    if (!activities) return;
    const now = Date.now();

    // Freshest pickup per task within the window.
    const freshest = new Map<string, { payload: ReturnType<typeof pickupToastFromActivity>; age: number }>();
    for (const a of activities) {
      const payload = pickupToastFromActivity(a);
      if (!payload) continue;
      const age = ageMs(a, now);
      if (age > FRESH_TOAST_MS) continue;
      const prev = freshest.get(payload.stableId);
      if (!prev || age < prev.age) freshest.set(payload.stableId, { payload, age });
    }

    for (const { payload } of freshest.values()) {
      if (!payload) continue;
      if (fired.current.get(payload.stableId) === payload.progress) continue; // unchanged → skip
      fired.current.set(payload.stableId, payload.progress);
      showPickupToast(payload);
    }
  }, [feed.data]);
}

/** Mountable observer (renders nothing) — placed once in the app shell. */
export function PickupToastObserver(): null {
  usePickupToastObserver();
  return null;
}
